// FATE override: make "−" at qty=1 remove the line item instead of no-op.
// Date: 2026-04-23
import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   *
   * FATE override: when the product's min is 1 (the common case), the minus
   * button stays ENABLED at qty=1 because clicking it emits quantity=0 =>
   * line removal. For products with min_order > 1, fall back to the native
   * behaviour (disabled at value <= min) so we never remove a line that
   * requires a higher minimum quantity.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (min <= 1) {
      minusButton.disabled = false;
    } else {
      minusButton.disabled = value <= min;
    }
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * FATE override: intercept decrement at qty === 1 (only when min === 1) and
   * emit a zero-quantity update event. component-cart-items.js#onQuantityChange
   * routes quantity=0 to onLineItemRemove, which handles the row animation +
   * /cart/change.js POST (action: 'clear'). No direct DOM mutation from here —
   * purely event-driven, preserves the morph pipeline.
   *
   * When the product has min_order > 1, DON'T convert the decrement into a
   * remove: the user would lose the line silently when they may have just
   * wanted to decrement within the legal range. Instead, defer to the parent
   * which clamps at the configured min (no-op if already at min).
   *
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    if (stepMultiplier < 0) {
      const { quantityInput } = this.refs;
      const { min, value } = this.getCurrentValues();
      if (min <= 1 && value <= 1) {
        const cartLine = Number(quantityInput.dataset.cartLine) || undefined;
        this.dispatchEvent(new QuantitySelectorUpdateEvent(0, cartLine));
        return;
      }
    }
    super.updateQuantity(stepMultiplier);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
